import { makeStyles } from "@material-ui/core"
import React, { useEffect, useState } from "react"
import { Button, TextButton } from "../../../components/button"
import { BackButton } from "../../../components/buttons/back_btn"
import { BaseContainer } from "../../../components/container"
import { countryList } from "../../../components/country_dropdown"
import { HeaderTitle } from "../../../components/page_sections"
import { StickyFooterGroup } from "../../../components/sticky_footer_group"
import Typography from "../../../components/typography"
import RadioCardGroup from '../../../components/radio_card_group';
import RadioCard from '../../../components/radio_card';
import { RecordContext, useRecordContext } from "../../../components/context/recordState"
import { RecordSteps } from "../../../components/context/recordReducer"
import { useHistory } from "react-router-dom"
import { countryHemisphereMap, HemisphereMap } from "./hemisphere_mappings"
import { DualFormulaContent, SingleFormulaContent } from "./formula_content"
import { InfoPanel } from "../../../components/info_panel"
import { CollapsedSection } from "../../../components/collapsed_section"

const useStyles = makeStyles((theme) => ({
    sectionGap: {
        marginTop: '20px'
    },
    disclaimerItem: {
        textAlign: "end"
    },
    suggestion: {
        display: "flex",
        justifyContent: "space-between",
        gap: theme.spacing(1),
        marginTop: theme.spacing(1),
    },
    suggestionContent: {
        textAlign: "right"
    },
    buttonContainer: {
        display: "grid",
        gridTemplateRows: "1fr 1fr",
        gap: "10px",
    },
    hemisphereButton: {
        display: "flex",
    },
}))

type Hemisphere = 'NORTH' | 'SOUTH'

const HemisphereContent: React.FC<{hemisphere: HemisphereMap}> = ({hemisphere}) => {
    if (hemisphere === HemisphereMap.DUAL) {
        return <DualFormulaContent />
    } else {
        return <SingleFormulaContent />
    }
}

export const SetHemisphere = () => {
    const classes = useStyles()
    const history = useHistory()

    const { state, dispatch } = useRecordContext(RecordContext)
    const countryHemisphere = countryHemisphereMap.get(state.country || '')
    const countryFullName = countryList.get(state.country || '')

    const setHemisphere = (event: React.MouseEvent<HTMLButtonElement>) => {
        const clickedButton = event.currentTarget as HTMLButtonElement;
        const hemisphere = clickedButton.value as Hemisphere;
        dispatch({
            type: RecordSteps.SET_HEMISPHERE,
            payload: {
                hemisphere: hemisphere
            }
        })
         history.push(`/add-record/${state.user.uuid}/date`)
    }

    return (
        <BaseContainer noHeader>
            <BackButton />
            <HeaderTitle>
                Which formula (season) vaccine was used?
            </HeaderTitle>
            <Typography variant="bodyTextMediumDark">
                Based on your location, we believe you took the following flu vaccine formula:
            </Typography>
            <div className={classes.sectionGap}>
                <InfoPanel severity="info" title="Formula suggestion">
                    <div className={classes.suggestion}>
                        <Typography variant="bodyTextSmall">
                            My vaccination location
                        </Typography>
                        <Typography variant="bodyTextMediumPrimary" className={classes.suggestionContent}>
                            {countryFullName}
                        </Typography>
                    </div>
                    <div className={classes.suggestion}>
                        <Typography variant="bodyTextSmall">
                            Seasonal vaccination formula in use
                        </Typography>
                        <Typography variant="bodyTextMediumPrimary" className={classes.suggestionContent}>
                            {countryHemisphere == 'NORTH' && 'Northern'}
                            {countryHemisphere == 'SOUTH' && 'Southern'}
                            {countryHemisphere == 'DUAL' && 'Northern & Southern'}
                        </Typography>
                    </div>
                </InfoPanel>
            </div>
            <div className={classes.sectionGap}>
            {countryHemisphere == 'NORTH' && (
                <Typography variant="bodyTextMediumDark">
                    This is the version designed to protect against the flu strains expected during the northern hemisphere flu season, typically <b>October - March.</b>
                </Typography>
            )}
            {countryHemisphere == 'SOUTH' && (
                <Typography variant="bodyTextMediumDark">
                    This is the version designed to protect against the flu strains expected during the southern hemisphere flu season, typically <b>April - September.</b>
                </Typography>
            )}
            {countryHemisphere == 'DUAL' && (
                <>
                    <div className={classes.sectionGap}>
                        <Typography variant="bodyTextMediumDark">
                            In {countryFullName} both the northern and southern hemisphere vaccines are in use.<br/>
                        </Typography>
                    </div>
                    <div className={classes.sectionGap}>
                        <Typography variant="bodyTextMediumDark">
                            The northern hemisphere vaccine is designed to target the flu strains expected
                            during the northern hemisphere flu season, typically <b>October - March.</b><br/>
                        </Typography>
                    </div>
                    <div className={classes.sectionGap}>
                        <Typography variant="bodyTextMediumDark">
                            The southern hemisphere vaccine is designed to target the flu strains expected
                            during the southern hemisphere flu season, typically <b>April - September.</b>
                        </Typography>
                    </div>
                </>
            )}
            </div>
            <div className={classes.sectionGap}>
                <Typography variant="bodyTextMediumPrimary">
                    Please choose below:
                </Typography>
            </div>
            {countryHemisphere == 'NORTH' && (
                <>
                    <div className={classes.sectionGap}>
                        <Button primary fullWidth onClick={setHemisphere} value="NORTH">
                            Yes, I took the Northern Hemisphere vaccine
                        </Button>
                    </div>
                    <div className={classes.sectionGap}>
                        <Button fullWidth onClick={setHemisphere} value="SOUTH">
                            No, I took the Southern Hemisphere vaccine
                        </Button>
                    </div>
                </>
            )}
            {countryHemisphere == 'SOUTH' && (
                <>
                    <div className={classes.sectionGap}>
                        <Button fullWidth onClick={setHemisphere} value="NORTH">
                            No, I took the Northern Hemisphere vaccine
                        </Button>
                    </div>
                    <div className={classes.sectionGap}>
                        <Button primary fullWidth onClick={setHemisphere} value="SOUTH">
                            Yes, I took the Southern Hemisphere vaccine
                        </Button>
                    </div>
                </>
            )}
            {countryHemisphere == 'DUAL' && (
                <div className={classes.sectionGap}>
                    <div className={classes.buttonContainer}>
                        <Button fullWidth className={classes.hemisphereButton} onClick={setHemisphere} value="NORTH">
                            I took the Northern Hemisphere vaccine
                        </Button>
                        <Button fullWidth className={classes.hemisphereButton} onClick={setHemisphere} value="SOUTH">
                            I took the Southern Hemisphere vaccine
                        </Button>
                    </div>
                </div>
            )}
            <div className={classes.sectionGap}>
                <TextButton onClick={() => {
                    history.push(`/add-record/${state.user.uuid}/hemisphere-cannot-proceed`)
                }}>
                    I don't know
                </TextButton>
            </div>
        </BaseContainer>
    )
}
